<script setup>
import { ref } from 'vue'
import { Head, Link, useForm } from '@inertiajs/vue3'
import GuestLayout from '@/Layouts/GuestLayout.vue'
import ApplicationLogo from '@/Components/ApplicationLogo.vue'
import SocialLoginButtons from '@/Components/SocialLoginButtons.vue'
import TextInput from '@/Components/TextInput.vue'
import InputError from '@/Components/InputError.vue'
import Checkbox from '@/Components/Checkbox.vue'

defineProps({
  canLogin: { type: Boolean, default: true },
  canRegister: { type: Boolean, default: true },
  canResetPassword: { type: Boolean, default: false },
  status: { type: String, default: '' }
})

const activeTab = ref('login')

const loginForm = useForm({
  email: '',
  password: '',
  remember: false
})

const registerForm = useForm({
  username: '',
  email: '',
  password: '',
  password_confirmation: ''
})

const submitLogin = () => {
  loginForm.post(route('login'), {
    onFinish: () => loginForm.reset('password')
  })
}

const submitRegister = () => {
  registerForm.post(route('register'), {
    onFinish: () => registerForm.reset('password', 'password_confirmation')
  })
}

const valueProps = [
  'Instant answers in real-time chat — no waiting, no ghosting',
  'Voice channels when typing just doesn\'t cut it',
  'Citations backed by actual research, not vibes',
  'A wiki so the good stuff never dies in a thread',
  'Zero algorithms. Zero ads. Just signal.'
]
</script>

<template>
  <Head title="AskBro" />

  <GuestLayout :header="false">
    <div class="relative flex min-h-screen flex-col bg-primary-950 text-primary-50">
      <div class="relative mx-auto flex w-full max-w-5xl flex-1 flex-col px-6 py-8 sm:px-8 lg:px-12">
        <!-- Centered header: logo + headline -->
        <header class="py-10 text-center lg:py-14">
          <Link href="/" class="inline-flex items-center justify-center">
            <ApplicationLogo class="h-10" large />
          </Link>

          <h1 class="!normal-case mt-8 text-4xl font-black leading-tight text-white sm:text-5xl">
            Ask better questions.<br />Keep the useful answers.
          </h1>
        </header>

        <!-- Two-column layout -->
        <div class="pb-12">
          <div class="grid w-full gap-10 lg:grid-cols-2 lg:gap-12">
            <!-- Left: pitch + value props -->
            <div class="flex flex-col justify-start">
              <p class="text-lg leading-8 text-primary-300">
                Your questions deserve better than a Reddit thread from 2014. AskBro is where the
                obsessives, the grinders, and the perpetually-curious go to get real answers — fast.
                Training, nutrition, biohacking, career, mindset. You name it, someone here has
                already tried it, tracked it, and lived to tell the tale.
              </p>

              <div class="mt-8 rounded-lg bg-primary-900 p-6">
                <ul class="flex flex-col gap-4">
                  <li
                    v-for="point in valueProps"
                    :key="point"
                    class="flex items-start gap-3 text-sm leading-6 text-primary-200"
                  >
                    <span class="mt-0.5 text-accent-500">—</span>
                    <span>{{ point }}</span>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Right: auth form -->
            <div class="flex flex-col justify-center">
              <div class="rounded-lg bg-primary-900 p-8">
                <!-- Tabs -->
                <div class="mb-8 flex gap-6 border-b border-primary-800">
                  <button
                    class="relative pb-3 text-sm font-semibold transition"
                    :class="activeTab === 'login' ? 'text-white' : 'text-primary-400 hover:text-primary-200'"
                    @click="activeTab = 'login'"
                  >
                    Sign in
                    <span
                      v-if="activeTab === 'login'"
                      class="absolute inset-x-0 bottom-0 h-0.5 bg-accent-500"
                    />
                  </button>
                  <button
                    class="relative pb-3 text-sm font-semibold transition"
                    :class="
                      activeTab === 'register' ? 'text-white' : 'text-primary-400 hover:text-primary-200'
                    "
                    @click="activeTab = 'register'"
                  >
                    Create account
                    <span
                      v-if="activeTab === 'register'"
                      class="absolute inset-x-0 bottom-0 h-0.5 bg-accent-500"
                    />
                  </button>
                </div>

                <!-- Status message -->
                <div
                  v-if="status"
                  class="mb-6 rounded-md bg-emerald-900/40 px-4 py-3 text-sm font-medium text-emerald-400"
                >
                  {{ status }}
                </div>

                <!-- Social login -->
                <SocialLoginButtons />

                <div class="my-6 flex items-center gap-4">
                  <div class="h-px flex-1 bg-primary-800" />
                  <span class="text-xs font-semibold uppercase tracking-[0.24em] text-primary-500">
                    Email
                  </span>
                  <div class="h-px flex-1 bg-primary-800" />
                </div>

                <!-- Login form -->
                <form v-if="activeTab === 'login'" class="space-y-5" @submit.prevent="submitLogin">
                  <div>
                    <label for="login-email" class="block text-sm font-semibold text-primary-200">
                      Email
                    </label>
                    <TextInput
                      id="login-email"
                      v-model="loginForm.email"
                      type="email"
                      autocomplete="email"
                      required
                      placeholder="you@example.com"
                      class="mt-2"
                    />
                    <InputError class="mt-2" :message="loginForm.errors.email" />
                  </div>

                  <div>
                    <div class="flex items-center justify-between gap-4">
                      <label for="login-password" class="block text-sm font-semibold text-primary-200">
                        Password
                      </label>
                      <Link
                        v-if="canResetPassword"
                        :href="route('password.request')"
                        prefetch
                        class="text-sm font-medium text-primary-400 transition hover:text-white"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <TextInput
                      id="login-password"
                      v-model="loginForm.password"
                      type="password"
                      autocomplete="current-password"
                      required
                      placeholder="Your password"
                      class="mt-2"
                    />
                    <InputError class="mt-2" :message="loginForm.errors.password" />
                  </div>

                  <label class="flex items-center gap-3 text-sm text-primary-300">
                    <Checkbox
                      v-model:checked="loginForm.remember"
                      class="!rounded !border-primary-600 !bg-primary-800 !text-accent-500 focus:!ring-accent-500/50"
                    />
                    Keep me signed in
                  </label>

                  <button
                    type="submit"
                    class="inline-flex w-full items-center justify-center rounded-md bg-accent-500 px-5 py-3 text-xs font-semibold uppercase tracking-widest text-primary-950 transition hover:bg-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-400 focus:ring-offset-2 focus:ring-offset-primary-900 disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="loginForm.processing"
                  >
                    Sign in
                  </button>
                </form>

                <!-- Register form -->
                <form v-if="activeTab === 'register'" class="space-y-5" @submit.prevent="submitRegister">
                  <div>
                    <label for="register-username" class="block text-sm font-semibold text-primary-200">
                      Username
                    </label>
                    <TextInput
                      id="register-username"
                      v-model="registerForm.username"
                      type="text"
                      autocomplete="username"
                      required
                      placeholder="pick_a_name"
                      class="mt-2"
                    />
                    <p class="mt-1.5 text-xs text-primary-500">This is what people will @mention.</p>
                    <InputError class="mt-2" :message="registerForm.errors.username" />
                  </div>

                  <div>
                    <label for="register-email" class="block text-sm font-semibold text-primary-200">
                      Email
                    </label>
                    <TextInput
                      id="register-email"
                      v-model="registerForm.email"
                      type="email"
                      autocomplete="email"
                      required
                      placeholder="you@example.com"
                      class="mt-2"
                    />
                    <InputError class="mt-2" :message="registerForm.errors.email" />
                  </div>

                  <div>
                    <label for="register-password" class="block text-sm font-semibold text-primary-200">
                      Password
                    </label>
                    <TextInput
                      id="register-password"
                      v-model="registerForm.password"
                      type="password"
                      autocomplete="new-password"
                      required
                      placeholder="Create a password"
                      class="mt-2"
                    />
                    <InputError class="mt-2" :message="registerForm.errors.password" />
                  </div>

                  <div>
                    <label
                      for="register-password-confirm"
                      class="block text-sm font-semibold text-primary-200"
                    >
                      Confirm password
                    </label>
                    <TextInput
                      id="register-password-confirm"
                      v-model="registerForm.password_confirmation"
                      type="password"
                      autocomplete="new-password"
                      required
                      placeholder="Repeat your password"
                      class="mt-2"
                    />
                    <InputError class="mt-2" :message="registerForm.errors.password_confirmation" />
                  </div>

                  <button
                    type="submit"
                    class="inline-flex w-full items-center justify-center rounded-md bg-accent-500 px-5 py-3 text-xs font-semibold uppercase tracking-widest text-primary-950 transition hover:bg-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-400 focus:ring-offset-2 focus:ring-offset-primary-900 disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="registerForm.processing"
                  >
                    Create account
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </GuestLayout>
</template>
